<template>
    <div
        v-if="loaded"
        class="my-3 rounded-md p-4 shadow dark:bg-gray-800"
    >
        <div class="flex items-center justify-between gap-3">
            <h3 class="text-sm font-bold text-gray-400">
                VALIDAÇÃO FACIAL (IDWALL)
            </h3>
            <span
                :class="badgeClass"
                class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
            >
                {{ stateLabel }}
            </span>
        </div>

        <dl class="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
                <dt class="text-gray-400">Validada em</dt>
                <dd class="dark:text-white">
                    {{ formatDate(validation.validated_at) || "—" }}
                </dd>
            </div>
            <div>
                <dt class="text-gray-400">Versão</dt>
                <dd class="dark:text-white">
                    {{ validation.idwall_version ? validation.idwall_version.toUpperCase() : "—" }}
                </dd>
            </div>
            <div>
                <dt class="text-gray-400">Resposta da IdWall</dt>
                <dd class="dark:text-white">{{ validation.idwall_status || "—" }}</dd>
            </div>
            <div>
                <dt class="text-gray-400">Tentativas</dt>
                <dd class="dark:text-white">{{ validation.attempts }}</dd>
            </div>
        </dl>

        <p
            v-if="validation.reason"
            class="mt-3 rounded bg-rose-50 p-2 text-sm text-rose-800"
        >
            <strong>Motivo:</strong> {{ validation.reason }}
        </p>

        <p
            v-else-if="validation.block_reason"
            class="mt-3 rounded p-2 text-sm"
            :class="reopenedByCompliance ? 'bg-amber-50 text-amber-800' : 'bg-rose-50 text-rose-800'"
        >
            <strong>{{ reopenedByCompliance ? "Reabertura:" : "Bloqueio:" }}</strong>
            {{ blockReasonLabel }}
        </p>

        <p
            v-if="validation.state === 'pending'"
            class="mt-3 text-xs text-gray-500"
        >
            O saque fica barrado até a IdWall responder.
        </p>

        <p
            v-else-if="validation.state === 'expired'"
            class="mt-3 text-xs text-gray-500"
        >
            A validação passou de {{ validation.ttl_days }} dias. O saque fica barrado até a
            pessoa refazer o reconhecimento facial na tela de saque.
        </p>

        <router-link
            :to="{ name: 'compliance.details', params: { user_id: userId } }"
            class="mt-3 inline-block text-xs font-medium text-indigo-600 hover:underline"
        >
            Abrir o compliance deste usuário
        </router-link>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "BiometricCheckCard",

    props: {
        userId: {
            type: [Number, String],
            required: true,
        },
    },

    data() {
        return {
            loaded: false,
            validation: {},
        };
    },

    computed: {
        // A coluna que guarda o motivo do bloqueio e a mesma que o botao do compliance usa para
        // dizer que reabriu a IdWall na mao. Sem esta distincao, quem reabriu um cadastro
        // aparece aqui como reprovado pela IdWall, sem nunca ter feito uma tentativa.
        reopenedByCompliance() {
            return this.validation.block_reason === "forced_by_admin";
        },

        stateLabel() {
            if (this.reopenedByCompliance) {
                return "Reaberta";
            }

            return {
                validated: "Aprovada",
                expired: "Vencida",
                blocked: "Reprovada",
                pending: "Aguardando",
                not_started: "Não iniciada",
            }[this.validation.state] ?? this.validation.state;
        },

        badgeClass() {
            if (this.reopenedByCompliance) {
                return "bg-amber-100 text-amber-800";
            }

            return {
                validated: "bg-green-100 text-green-800",
                expired: "bg-orange-100 text-orange-800",
                blocked: "bg-rose-100 text-rose-800",
                pending: "bg-yellow-100 text-yellow-800",
                not_started: "bg-gray-100 text-gray-600",
            }[this.validation.state] ?? "bg-gray-100 text-gray-600";
        },

        blockReasonLabel() {
            return {
                qsa_failed_idwall: "Quadro societário reprovado pela IdWall",
                rejected: "Reprovado pela IdWall",
                inconclusive_max_attempts: "Inconclusivo após três tentativas",
                forced_by_admin:
                    "IdWall reaberta manualmente pelo compliance. Não houve reprovação: a pessoa " +
                    "refaz o reconhecimento facial no próximo saque.",
                inconclusive: "Inconclusivo, aguardando novo envio",
            }[this.validation.block_reason] ?? this.validation.block_reason;
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        fetch() {
            axios
                .get(`/api/kyc/biometric-revalidation/${this.userId}`)
                .then(({ data }) => {
                    this.validation = data?.data?.last_facial_validation ?? {};
                    this.loaded = Boolean(this.validation.state);
                })
                .catch(() => {
                    this.loaded = false;
                });
        },

        formatDate(value) {
            if (!value) return null;

            return new Date(value).toLocaleString("pt-BR");
        },
    },
};
</script>
